import {Col, ComponentLoader, Row} from '@snsw-gel/react';
import {scrollToTop} from '@gaas/gaas-react-component-library';
import withLayout from '../../components/Layout/WithLayout';
import React, {useContext, useEffect, useState} from 'react';
import DefaultNoStepperLayout from '../../components/Layout/DefaultNoStepper';
import {AccountMetadata, LinkedAccount, TollRegistrationContext, TollReliefScheme} from '../../context/ApplicationContext';
import Personal from './Personal';
import TollAccountType from '../../types/tollAccountType';
import TollReliefConfig from '../../config/TollReliefConfig';
import RemoveTollAccountConfirmationModal from './RemoveTollAccountConfirmationModal';
import CannotLinkModal from './CannotLinkModal';
import SoleTrader from './SoleTrader';
import ErrorAlert, {ErrorReason} from './ErrorAlert';
import SuccessfulLinkAlert from './SuccessfulLinkAlert';
import SuccessfulUnlinkAlert from './SuccessfulUnlinkAlert';
import ManageTollAccountNavigationButton from './ManageTollAccountNavigationButton';
import useTrConfig from 'src/hooks/useTrConfig';
import {linkedTollAccountsApi, unlinkAccount} from '../../services/api-services';
import routes from '../../routes';
import {useHistory} from 'react-router-dom';
import {useCookies} from 'react-cookie';
import CannotUnlinkModal from './CannotUnlinkModal';
import {userInfo} from 'src/utils/sessionStorage';
import {EventData, generateEventData} from 'src/utils/ga-page-tracking';
import {useTollProviderOutageCheck} from '../../hooks/features/useTollProviderOutageCheck';
import {TSPOutageAlert} from './TSPOutageAlert';
import {FeatureToggleName} from '../../hooks/features/useBaseFeature';
import TollProvider from '../../types/TollProvider';
import {useAlertMessageCheck} from '../../hooks/features/useAlertMessageCheck';
import {AlertMessage} from './AlertMessage';

const ManageTollAccountPage: React.FC = () => {
    const history = useHistory();
    const [cookies] = useCookies([TollReliefConfig.tr3TermsDecisionCookie.name]);
    const {
        linkedTollAccounts,
        setLinkedTollAccounts,
        successfullyLinkedAccountType,
    } = useContext(TollRegistrationContext);
    // There should be at most one toll account per type.
    const personalTollAccount = linkedTollAccounts.find(
        (tollAccount) => tollAccount.tollAccountType === TollAccountType.Personal
    );
    const hasPersonal = personalTollAccount !== undefined;
    const mixedTollAccount = linkedTollAccounts.find(
        (tollAccount) => tollAccount.tollAccountType === TollAccountType.Mixed
    );
    const hasMixed = mixedTollAccount !== undefined;
    const businessTollAccount = linkedTollAccounts.find(
        (tollAccount) => tollAccount.tollAccountType === TollAccountType.Business
    );
    const hasBusiness = businessTollAccount !== undefined;

    const hasEtollLink = linkedTollAccounts.find(
        (tollAccount) => tollAccount.tollProvider === TollProvider.EToll
    ) !== undefined;
    const hasLinktLink = linkedTollAccounts.find(
        (tollAccount) => tollAccount.tollProvider === TollProvider.Linkt
    ) !== undefined;

    /** For blocking other api calls, and setting a loading spiral on whole screen. */
    const [isApiInProgress, setIsApiInProgress] = useState(false);

    // Modal showers
    /** A toll account that is being removed on an unlink click. By not being undefined, assume to show the modal. */
    const [tollAccountForRemoval, setTollAccountForRemoval] = useState<LinkedAccount | undefined>(undefined);
    const showRemoveTollAccountModal = tollAccountForRemoval !== undefined;
    /** A toll account type that is trying to be linked, but is not allowed */
    const [unlinkableTollAccountType, setUnlinkableTollAccountType] = useState<TollAccountType | undefined>(undefined);
    const showCannotLinkModal = unlinkableTollAccountType !== undefined;
    /** A toll account that has a claim in a status that would block unlinking personal */
    const [showCannotUnlinkDueToProcessingClaimModal, setShowCannotUnlinkDueToProcessingClaimModal] = useState<boolean>(false);

    /** For handling error messages */
    const [errorReason, setErrorReason] = useState<ErrorReason | undefined>(undefined);

    const [successfullyUnlinkedAccountType, setSuccessfullyUnlinkedAccountType] = useState<TollAccountType | undefined>(undefined);

    const {data: tollProviderOutage, isFeatureQueryLoading: isTSPOutageLoading, isFeatureEnabled } = useTollProviderOutageCheck();
    const [isTr2Decommissioned, setIsTr2Decommissioned] = useState<boolean>(false); //TODO remove after tr2 decomm
    const {data: alertMessages, isFeatureQueryLoading: isAlertLoading } = useAlertMessageCheck();

    const shouldShowEtollOutage = (tollProviderOutage?.displayEtollOutage ?? false) &&
        (hasEtollLink || !hasLinktLink);
    const shouldShowLinktOutage = (tollProviderOutage?.displayLinktOutage ?? false) &&
        (hasLinktLink || !hasEtollLink);

    useEffect(() => {
        if(tollProviderOutage?.displayEtollOutage === true) {
            scrollToTop();
        }
    }, [tollProviderOutage?.displayEtollOutage]);
    useEffect(() => {
        if(tollProviderOutage?.displayLinktOutage === true) {
            scrollToTop();
        }
    }, [tollProviderOutage?.displayLinktOutage]);

    useEffect(() => {
        if(alertMessages?.displayAlertMessage === true) {
            scrollToTop();
        }
    }, [alertMessages?.displayAlertMessage]);

    const userInfoObj = userInfo();
    useEffect(() => {
        // Simulating data for the event
        const eventData: EventData = {
            page_title: 'Manage Toll Account',
            transaction_info: {
                transaction_step: 'Manage Toll Account',
            },
            user_info: {
                user_id: userInfoObj && userInfoObj.id ? userInfoObj.id : 'not set',
                user_login_status: userInfoObj && userInfoObj.id ? 'fully authenticated' : 'not authenticated',
            },
        };

        // Generating and pushing the event data to the dataLayer
        generateEventData(eventData);
    }, [userInfoObj]);

    function onLinkPersonal() {
        if (hasMixed) {
            // Cannot link personal when mixed exists
            setUnlinkableTollAccountType(TollAccountType.Personal);
        } else {
            history.push(routes.personalTollAccountDetails.path);
        }
    }

    function onUnlinkPersonal() {
        if(!personalTollAccount) {
            return;
        }
        if(hasClaimStatusThatBlocksUnlinking(personalTollAccount.accountMetadata)) {
            setShowCannotUnlinkDueToProcessingClaimModal(true);
        } else {
            setTollAccountForRemoval(personalTollAccount);
        }
    }

    function hasClaimStatusThatBlocksUnlinking(accountMetadata: AccountMetadata[]): boolean {
        return accountMetadata?.some(metadata => metadata === AccountMetadata.ACTIVELY_PROCESSING_CLAIM_BLOCKS_UNLINKING) ?? false;
    }

    function onLinkMixed() {
        if (hasPersonal || hasBusiness) {
            // Cannot link a mixed when either personal or business exist
            setUnlinkableTollAccountType(TollAccountType.Mixed);
        } else {
            history.push(routes.mixedTollAccountDetails.path);
        }
    }

    function onUnlinkMixed() {
        setTollAccountForRemoval(mixedTollAccount);
    }

    function onLinkBusiness() {
        if (hasMixed) {
            // Cannot link business when mixed exists
            setUnlinkableTollAccountType(TollAccountType.Business);
        } else {
            history.push(routes.businessTollAccountDetails.path);
        }
    }

    function onUnlinkBusiness() {
        setTollAccountForRemoval(businessTollAccount);
    }

    /**
     * Perform the actual unlinking action on the tollAccountForRemoval.
     * This is after the user has confirmed the removal.
     */
    async function onUnlinkConfirm() {
        if (tollAccountForRemoval === undefined) {
            console.error('Somehow trying to unlink nonexistent account');
            return;
        }

        setIsApiInProgress(true);

        const unlinkResponse = await unlinkAccount(tollAccountForRemoval);
        if (unlinkResponse.code === 'SUCCESS') {
            setSuccessfullyUnlinkedAccountType(tollAccountForRemoval?.tollAccountType);
            // Remove the unlinked account from context
            const accountsWithoutUnlinkedAccount = linkedTollAccounts.filter((account) => {
                return tollAccountForRemoval.participantToken !== account.participantToken;
            });
            setLinkedTollAccounts(accountsWithoutUnlinkedAccount);
        } else {
            setErrorReason('unlinkFailure');
        }

        setIsApiInProgress(false);
        setTollAccountForRemoval(undefined);
        scrollToTop();
    }

    /**
     * Cancel the unlinking. User has changed their mind.
     */
    function onUnlinkCancel() {
        // closes the confirmation modal
        setTollAccountForRemoval(undefined);
    }
    /**
     * For when user closes the Cannot link modal.
     */
    function onCannotLinkModalConfirm() {
        setUnlinkableTollAccountType(undefined);
    }

    /**
     * For when user closes the Cannot unlink modal.
     */
    function onCannotUnlinkModalConfirm() {
        setShowCannotUnlinkDueToProcessingClaimModal(false);
    }

    useEffect(() => {
        scrollToTop();
    },[]);

    // On component render, call api to populate toll accounts.
    useEffect(() => {
        if(isTSPOutageLoading) {
            return;
        }
        setIsApiInProgress(true);
        linkedTollAccountsApi().then( apiResponse => {
            if (apiResponse === 'userHasOtherMyAccount') {
                setErrorReason('existingAccount');
            } else if (apiResponse === 'generic') {
                setErrorReason('genericError');
            } else {
                // success
                setLinkedTollAccounts(apiResponse);

                const tr2DecomFeatureEnabled = isFeatureEnabled(FeatureToggleName.TR2_DECOMMISSION);
                setIsTr2Decommissioned(tr2DecomFeatureEnabled);

                if(!tr2DecomFeatureEnabled) {
                    // TODO: include a check here for if user has previously denied consent

                    // Moving redirection here, to make sure it only happen after api call is done.
                    // Otherwise, when "Remove Account" on New TC page redirect to MTA, it redirects back again to New TC page.

                    // For personal, check if there are any accounts that are only tr2
                    const onlyTR2Account = apiResponse.find((account) => {
                        return account.tollAccountType === TollAccountType.Personal &&
                            account.schemes.includes(TollReliefScheme.TR2) &&
                            !account.schemes.includes(TollReliefScheme.TR3);
                    });

                    if (onlyTR2Account !== undefined) {
                        const tr3TermsDecisionCookie = cookies[TollReliefConfig.tr3TermsDecisionCookie.name];
                        if (tr3TermsDecisionCookie === undefined) {
                            // user has not yet acknowledged tr3 terms and conditions - go to terms and conditions page
                            history.push(routes.tr3TermsConditionsPage.path);
                        }
                    }
                }
            }
            setIsApiInProgress(false);
        });
    }, [cookies, setLinkedTollAccounts, history, isTSPOutageLoading]);

    const trConfig = useTrConfig();

    return <Row>
        <Col span={8}>

            {/** For use when api calls are in flight */}
            <ComponentLoader active={isApiInProgress || trConfig.isLoading || isTSPOutageLoading} fullPage label={TollReliefConfig.labels.componentLoader} />

            {shouldShowEtollOutage &&
                <TSPOutageAlert title='E-Toll outage' messages={tollProviderOutage!.etollMessaging ?? []}/>
            }
            {shouldShowLinktOutage &&
                <TSPOutageAlert title='Linkt outage' messages={tollProviderOutage!.linktMessaging ?? []}/>
            }
            {alertMessages?.displayAlertMessage && alertMessages.alertMessaging?.map((alert, index) => (
                <AlertMessage
                    key={`${alert.title}-${index}`}
                    title={alert.title}
                    messages={alert.messages}
                    messageVariant={alert.variant}
                />
            ))}

            {/* Modals */}
            {showRemoveTollAccountModal && <RemoveTollAccountConfirmationModal
                tollAccount={tollAccountForRemoval}
                onUnlink={onUnlinkConfirm}
                onCancel={onUnlinkCancel}
            />}

            {showCannotLinkModal && <CannotLinkModal
                tollAccountType={unlinkableTollAccountType}
                onConfirm={onCannotLinkModalConfirm}
            />}

            {showCannotUnlinkDueToProcessingClaimModal && <CannotUnlinkModal
                onConfirm={onCannotUnlinkModalConfirm}
            />}

            {/** For use when api calls fail */}
            {errorReason !== undefined && <ErrorAlert errorReason={errorReason} />}

            {/** For use when a link was successful and user was redirected here */}
            {successfullyLinkedAccountType !== undefined && <SuccessfulLinkAlert tollAccountType={successfullyLinkedAccountType} />}

            {successfullyUnlinkedAccountType !== undefined &&
                <SuccessfulUnlinkAlert tollAccountType={successfullyUnlinkedAccountType} />}

            {/* The existingAccount error is unique and hide the rest of page content */}
            {(errorReason !== 'existingAccount') &&
            <>
                <Personal
                    weeklyCap={trConfig.getTR3weeklyCap()}
                    tollAccountNumber={personalTollAccount?.tollAccountNumber}
                    tollProvider={personalTollAccount?.tollProvider}
                    hasMixed={hasMixed}
                    onLink={onLinkPersonal}
                    onUnlink={onUnlinkPersonal}
                    processingClaimsBlocksUnlinking={hasClaimStatusThatBlocksUnlinking(personalTollAccount?.accountMetadata ?? [])}
                    isTr2Decommissioned={isTr2Decommissioned}
                />

                {!isTr2Decommissioned && // TODO remove this block after tr2 decomm
                    <SoleTrader
                        weeklyCap={trConfig.getTR3weeklyCap()}
                        mixedAccount={mixedTollAccount}
                        onMixedLink={onLinkMixed}
                        onMixedUnlink={onUnlinkMixed}
                        businessAccount={businessTollAccount}
                        onBusinessLink={onLinkBusiness}
                        onBusinessUnlink={onUnlinkBusiness}
                        hasPersonal={hasPersonal}
                    />
                }
            </>}

            <ManageTollAccountNavigationButton hasAnyLinkedAccounts={hasPersonal || hasBusiness || hasMixed} />
        </Col>
    </Row>;
};

export default withLayout(ManageTollAccountPage, DefaultNoStepperLayout, {
    headerTitle: 'Manage toll accounts'
});
